import { createSignal, onCleanup, onMount } from "solid-js";

export type Theme = "light" | "dark";
export type ThemePref = Theme | "system";

const STORAGE_KEY = "saquei.theme";

function readPref(): ThemePref {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v === "light" || v === "dark" || v === "system") return v;
    } catch {}
    return "system";
}

function systemTheme(): Theme {
    if (typeof window === "undefined") return "light";
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function effective(pref: ThemePref): Theme {
    return pref === "system" ? systemTheme() : pref;
}

function applyToDocument(theme: Theme) {
    document.documentElement.setAttribute("data-theme", theme);
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) meta.content = theme === "dark" ? "#0E1419" : "#FF6B35";
}

/** Aplica o tema persistido o quanto antes -- chamado em main.tsx */
export function bootstrapTheme(): void {
    if (typeof window === "undefined") return;
    applyToDocument(effective(readPref()));
}

/**
 * Hook reactive: retorna o pref atual + setter. Sincroniza com localStorage,
 * outras abas (storage event) e prefers-color-scheme se estiver em "system".
 */
export function useThemePref() {
    const [pref, setPrefSignal] = createSignal<ThemePref>(readPref());

    function setPref(next: ThemePref) {
        setPrefSignal(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch {}
        applyToDocument(effective(next));
    }

    onMount(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key !== STORAGE_KEY) return;
            const v = readPref();
            setPrefSignal(v);
            applyToDocument(effective(v));
        };
        window.addEventListener("storage", onStorage);

        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const onMq = () => { if (pref() === "system") applyToDocument(systemTheme()); };
        mq.addEventListener("change", onMq);

        onCleanup(() => {
            window.removeEventListener("storage", onStorage);
            mq.removeEventListener("change", onMq);
        });
    });

    return { pref, setPref, effective: () => effective(pref()) };
}
